import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Resolver, UseFormReturn } from 'react-hook-form';
import type { AnalyticsManager } from '@/analytics/AnalyticsManager';
import type { IDocumentScheme } from '@models/document/IDocumentScheme';
import type { IDocumentData } from '@models/document/IDocumentData';
import type { IFieldElem } from '@models/IFormData';
import type { ChangeRuntime } from '@utils/managers/ChangeRuntime';
import type { SaveChangeField } from '@utils/managers/ChangesManager';
import {
    makeDocumentResolver,
    buildDocumentValidationIndex,
    createDocumentResolverTelemetryReporter,
    createFormulaManagerEvaluator,
    initBooleanDefaults,
    runInvokeChangesInit,
    type IDocumentResolverContext,
    type IDocumentValidationIndex,
    type IDebugValidationFlags,
} from '@utils/validation/document';
import { createWorkCalendarHolidayChecker } from '@utils/validation/document/workCalendarProvider';
import type { ICentralizedInitExecutedKeys, IDocumentImperativeValidators } from '@/context/Contexts';
import { createValueStore, type IReadonlyValueStore } from '@srm/ui/lib/valueStore';
import type { IFieldOverrides } from '@/context/DebugPanelContext';

/**
 * Единая обвязка resolver-валидации документа для `Document` и `DocumentModal`.
 *
 * Хук вызывается ДО `useForm` (resolver нужен в его конфиге). Контекст resolver
 * читается лениво через ref-getter и обновляется каждый рендер вызовом
 * {@link IUseDocumentResolverResult.setResolverContext} — уже после того, как известны
 * mode/debug/scope. Стартовые side-effects (boolean-init + invokeChanges) выносит
 * отдельный {@link useCentralizedDocumentInit}, т.к. ему нужен уже созданный `useForm`.
 */
export interface IUseDocumentResolverParams {
    /** Единый флаг resolver-валидации (isDocumentResolverValidationEnabled). */
    enabled: boolean;
    scheme?: IDocumentScheme;
    /** Runtime-карта key → {index, name, value}. */
    fields?: Record<string, IFieldElem>;
    analytics: Pick<AnalyticsManager, 'trackEvent'>;
    telemetryEnabled: boolean;
}

/** Часть контекста resolver, известная только после инициализации формы/debug-панели. */
export interface IDocumentResolverRuntime {
    isEdit: boolean;
    isNew: boolean;
    debug?: IDebugValidationFlags;
    /** Дебаг-оверрайды правил из «жучка»: резолвер применяет их как контролы UI. */
    fieldOverrides?: ReadonlyMap<string, IFieldOverrides>;
    /** Документ + пользователь: смена scope немедленно очищает сетевой TTL-cache. */
    requestCacheScope: string;
}

export interface IUseDocumentResolverResult {
    /** Передаётся в `useForm({ resolver })`; undefined, если resolver выключен. */
    resolver: Resolver<IDocumentData> | undefined;
    /** Индекс контролов/зависимостей для DocumentValidationIndexContext (null, если выключен). */
    validationIndex: IDocumentValidationIndex | null;
    /** Обновить контекст resolver текущего рендера. Вызывать в теле рендера. */
    setResolverContext: (runtime: IDocumentResolverRuntime) => void;
    /** Сигналы FormProviderControl о начале/конце submit-валидации всей формы. */
    onSubmitValidationStart: () => void;
    onSubmitValidationEnd: () => void;
    /** API для DocumentImperativeValidatorsContext (валидаторы mount-bound контролов). */
    imperativeValidators: IDocumentImperativeValidators;
}

export const useDocumentResolver = ({
    enabled,
    scheme,
    fields,
    analytics,
    telemetryEnabled,
}: IUseDocumentResolverParams): IUseDocumentResolverResult => {
    const telemetryReporter = useMemo(
        () => (telemetryEnabled && scheme ? createDocumentResolverTelemetryReporter(analytics, scheme) : undefined),
        [analytics, telemetryEnabled, scheme],
    );
    const getHolidayChecker = useMemo(() => createWorkCalendarHolidayChecker(), []);
    const fullValidationRuns = useRef(0);
    const validationIndex = useMemo(
        () => (enabled && scheme && fields ? buildDocumentValidationIndex(scheme, fields) : null),
        [enabled, scheme, fields],
    );
    const resolverCtxRef = useRef<IDocumentResolverContext | undefined>(undefined);
    const resolverRef = useRef<Resolver<IDocumentData> | undefined>(undefined);
    if (enabled && !resolverRef.current) {
        resolverRef.current = makeDocumentResolver(() => resolverCtxRef.current);
    }

    // Императивные валидаторы mount-bound контролов (вложения): контролы регистрируются через
    // контекст, resolver читает актуальную карту через getter — реестр живёт на ref.
    const imperativeValidatorsRef = useRef(new Map<string, () => boolean | undefined>());
    const imperativeValidators = useMemo<IDocumentImperativeValidators>(
        () => ({
            register: (path, validate) => {
                imperativeValidatorsRef.current.set(path, validate);
                return () => {
                    if (imperativeValidatorsRef.current.get(path) === validate) {
                        imperativeValidatorsRef.current.delete(path);
                    }
                };
            },
        }),
        [],
    );

    const setResolverContext = useCallback(
        (runtime: IDocumentResolverRuntime) => {
            resolverCtxRef.current =
                enabled && scheme && fields
                    ? {
                          scheme,
                          fields,
                          isEdit: runtime.isEdit,
                          isNew: runtime.isNew,
                          debug: runtime.debug,
                          fieldOverrides: runtime.fieldOverrides,
                          getHolidayChecker,
                          validationIndex: validationIndex ?? undefined,
                          shouldValidateAll: () => fullValidationRuns.current > 0,
                          requestCacheScope: runtime.requestCacheScope,
                          onValidationRun: telemetryReporter,
                          getImperativeValidators: () => imperativeValidatorsRef.current,
                      }
                    : undefined;
        },
        [enabled, scheme, fields, getHolidayChecker, validationIndex, telemetryReporter],
    );

    const onSubmitValidationStart = useCallback(() => {
        fullValidationRuns.current += 1;
    }, []);
    const onSubmitValidationEnd = useCallback(() => {
        fullValidationRuns.current = Math.max(0, fullValidationRuns.current - 1);
    }, []);

    return {
        resolver: enabled ? resolverRef.current : undefined,
        validationIndex,
        setResolverContext,
        onSubmitValidationStart,
        onSubmitValidationEnd,
        imperativeValidators,
    };
};

const INIT_SIGNAL_SENTINEL = Symbol('document-init-uninitialized');

export interface IUseCentralizedDocumentInitParams {
    enabled: boolean;
    /** Готовность к запуску (у Document — загрузка завершена и turnOff известен). */
    ready: boolean;
    scheme?: IDocumentScheme;
    fields?: Record<string, IFieldElem>;
    formMethods: UseFormReturn<IDocumentData>;
    isEdit: boolean;
    isNew: boolean;
    /** turnOffInvokeChangesOnNew — глушит invokeChangesOnNew (черновик/после сабмита). */
    turnOff: boolean;
    onSaveField: SaveChangeField;
    setError: (errors?: string[]) => void;
    /** Ключ дедупликации «инициализация один раз»: у Document — версия, у Modal — сам fields. */
    initSignal: unknown;
    /** Смена документа: немедленно отменяет незавершённые каскады прошлого документа. */
    cancelSignal?: unknown;
    changeRuntime?: ChangeRuntime;
}

/**
 * Один раз (на каждый `initSignal`) прогоняет стартовые каскады документа независимо от
 * маунта вкладок.
 *
 * Отмена сделана намеренно БЕЗ cleanup (иначе прогон рвался бы при любом изменении deps —
 * догрузке external-data-source, которая меняет ссылку `fields` без смены версии, и при
 * StrictMode-remount — и не перезапускался бы из-за дедупа по `initSignal`). Вместо этого:
 *  - смена `initSignal` (новая генерация полей) бампает token в теле → прежний каскад с другим
 *    token прекращается, запускается новый;
 *  - смена ЗНАЧЕНИЯ `cancelSignal` (смена документа) бампает token немедленно, ещё до прихода
 *    новых полей, чтобы поздний каскад прошлого документа не писал в уже сброшенную форму.
 * StrictMode-remount и не-документные изменения deps ни `initSignal`, ни `cancelSignal` не меняют,
 * поэтому текущий каскад переживает их и доходит до конца.
 */
export const useCentralizedDocumentInit = ({
    enabled,
    ready,
    scheme,
    fields,
    formMethods,
    isEdit,
    isNew,
    turnOff,
    onSaveField,
    setError,
    initSignal,
    cancelSignal,
    changeRuntime,
}: IUseCentralizedDocumentInitParams): {
    /** Признак «стартовый проход завершён» — для CentralizedInitCompleteContext; значение — useSyncExternalStore */
    initCompleteStore: IReadonlyValueStore<boolean>;
    executedKeys: ICentralizedInitExecutedKeys;
} => {
    const lastInitSignal = useRef<unknown>(INIT_SIGNAL_SENTINEL);
    const lastCancelSignal = useRef<unknown>(cancelSignal);
    const runToken = useRef(0);
    // initSignal завершённого прохода. Признак «завершён» — совпадение с ТЕКУЩИМ initSignal: в рендере смены
    // режима сигнал уже новый, и гейт контролов закрыт сразу, а не после эффекта, сбрасывающего стор, — эффекты
    // детей идут раньше, и поле с invokeChangesOnEdit иначе запускало бы каскад вслед за проходом второй раз.
    const [completedSignal] = useState(() => createValueStore<unknown>(INIT_SIGNAL_SENTINEL));
    const initCompleteStore = useMemo<IReadonlyValueStore<boolean>>(
        () => ({ subscribe: completedSignal.subscribe, get: () => completedSignal.get() === initSignal }),
        [completedSignal, initSignal],
    );
    // Реестр «стартовый каскад выполнен» на текущее поколение документа: наполняется проходом,
    // читается и дополняется гейтом контролов (первый видимый маунт скрытого на ините контрола).
    const executedKeysRef = useRef<Set<string>>(new Set());
    const executedKeys = useMemo<ICentralizedInitExecutedKeys>(
        () => ({
            has: (key: string) => executedKeysRef.current.has(key),
            add: (key: string) => {
                executedKeysRef.current.add(key);
            },
        }),
        [],
    );

    useEffect(() => {
        if (!enabled) return;

        // Смена документа: отменяем незавершённый каскад прошлого документа сразу (до прихода
        // новых полей), но только когда cancelSignal реально изменил значение.
        if (lastCancelSignal.current !== cancelSignal) {
            lastCancelSignal.current = cancelSignal;
            runToken.current += 1;
            executedKeysRef.current.clear();
            completedSignal.set(INIT_SIGNAL_SENTINEL);
        }

        if (!ready || !scheme || !fields) return;
        if (lastInitSignal.current === initSignal) return;
        lastInitSignal.current = initSignal;

        const token = ++runToken.current;
        const isCurrent = () => runToken.current === token && lastInitSignal.current === initSignal;

        executedKeysRef.current.clear();
        completedSignal.set(INIT_SIGNAL_SENTINEL);
        initBooleanDefaults(scheme, fields, formMethods, { isNew, isEdit });
        const evaluator = createFormulaManagerEvaluator(fields, formMethods, isEdit, isNew);
        runInvokeChangesInit(scheme, fields, formMethods, evaluator, {
            isNew,
            isEdit,
            turnOff,
            onSave: onSaveField,
            changeRuntime,
            setError,
            isCurrent,
            // Поздний колбэк отменённого прогона не должен пачкать реестр нового поколения.
            onExecuted: (key) => {
                if (isCurrent()) executedKeysRef.current.add(key);
            },
        })
            .catch((error) => console.error('runInvokeChangesInit', error))
            .finally(() => {
                if (isCurrent()) completedSignal.set(initSignal);
            });
    }, [
        enabled,
        ready,
        scheme,
        fields,
        formMethods,
        isEdit,
        isNew,
        turnOff,
        onSaveField,
        setError,
        initSignal,
        cancelSignal,
        changeRuntime,
        completedSignal,
    ]);

    return { initCompleteStore, executedKeys };
};
